import { useMemo } from 'react'
import {
  WhatIfScenario,
  MECETable,
  MECEUserState
} from '@models/index'
import { compareScenarios, PropagationChain, MonteCarloResult, getSimulationRiskLevel } from '@utils/scenarioCalculator'
import './ScenarioComparisonView.css'

interface ScenarioComparisonViewProps {
  scenarioA: WhatIfScenario
  scenarioB: WhatIfScenario
  meceTable: MECETable
  userState?: MECEUserState | null
  onClose: () => void
  propagationChains?: PropagationChain[]
  simulationResults?: MonteCarloResult[]
}

export function ScenarioComparisonView({
  scenarioA,
  scenarioB,
  meceTable,
  userState,
  onClose,
  propagationChains,
  simulationResults
}: ScenarioComparisonViewProps) {
  const comparison = useMemo(
    () => compareScenarios(scenarioA, scenarioB, meceTable, userState),
    [scenarioA, scenarioB, meceTable, userState]
  )

  const formatDelta = (value: number, unit: string = '', inverse: boolean = false) => {
    if (value === 0) return <span className="delta-neutral">No change</span>
    const isPositive = inverse ? value < 0 : value > 0
    const sign = value > 0 ? '+' : ''
    return (
      <span className={`delta-value ${isPositive ? 'positive' : 'negative'}`}>
        {sign}{value}{unit}
      </span>
    )
  }

  const getTacticName = (pillarIndex: number, tacticIndex: number): string => {
    const pillar = meceTable.pillars[pillarIndex]
    if (!pillar) return 'Unknown Tactic'
    const tactic = pillar.tactics[tacticIndex]
    return tactic?.title || 'Untitled Tactic'
  }

  const getPillarName = (pillarIndex: number): string => {
    return meceTable.pillars[pillarIndex]?.name || 'Unknown Pillar'
  }

  return (
    <div className="scenario-comparison-view">
      <div className="comparison-header">
        <h3>Scenario Comparison</h3>
        <button className="btn-close-comparison" onClick={onClose} aria-label="Close comparison">
          x
        </button>
      </div>

      {/* Scenario Names */}
      <div className="comparison-scenarios">
        <div className="comparison-scenario scenario-a">
          <span className="scenario-label">A</span>
          <span className="scenario-name">{scenarioA.name}</span>
        </div>
        <span className="vs-indicator">vs</span>
        <div className="comparison-scenario scenario-b">
          <span className="scenario-label">B</span>
          <span className="scenario-name">{scenarioB.name}</span>
        </div>
      </div>

      {/* Key Metrics Comparison */}
      <div className="comparison-metrics">
        <div className="metric-comparison">
          <div className="metric-header">Coverage</div>
          <div className="metric-values">
            <span className="value-a">
              {Math.round((comparison.metrics_a.included_tactics / comparison.metrics_a.total_tactics) * 100)}%
            </span>
            <span className="metric-arrow">→</span>
            <span className="value-b">
              {Math.round((comparison.metrics_b.included_tactics / comparison.metrics_b.total_tactics) * 100)}%
            </span>
          </div>
          <div className="metric-delta">
            {formatDelta(comparison.differences.coverage_delta, '%')}
          </div>
        </div>

        <div className="metric-comparison">
          <div className="metric-header">Risk Score</div>
          <div className="metric-values">
            <span className="value-a">{comparison.metrics_a.risk_score}</span>
            <span className="metric-arrow">→</span>
            <span className="value-b">{comparison.metrics_b.risk_score}</span>
          </div>
          <div className="metric-delta">
            {formatDelta(comparison.differences.risk_delta, '', true)}
          </div>
        </div>

        <div className="metric-comparison">
          <div className="metric-header">Strategic Alignment</div>
          <div className="metric-values">
            <span className="value-a">{comparison.metrics_a.strategic_alignment_score}%</span>
            <span className="metric-arrow">→</span>
            <span className="value-b">{comparison.metrics_b.strategic_alignment_score}%</span>
          </div>
          <div className="metric-delta">
            {formatDelta(
              comparison.metrics_b.strategic_alignment_score - comparison.metrics_a.strategic_alignment_score,
              '%'
            )}
          </div>
        </div>
      </div>

      {/* Phase 5: BSC Causal Propagation */}
      {propagationChains && propagationChains.length > 0 && (
        <div className="propagation-section">
          <h4>BSC Causal Propagation</h4>
          <div className="propagation-chains">
            {propagationChains.map((chain, idx) => (
              <div key={idx} className="propagation-chain">
                <div className="chain-source">
                  <span className="perspective-badge">{chain.source_perspective}</span>
                  <span className="node-name">{chain.source_node}</span>
                </div>
                <div className="chain-impacts">
                  {chain.impacts.map((impact, iIdx) => (
                    <div key={iIdx} className="chain-impact">
                      <span className="impact-arrow">{impact.direction === 'positive' ? '\u2197' : '\u2198'}</span>
                      <span className="perspective-badge">{impact.target_perspective}</span>
                      <span className="node-name">{impact.target_node}</span>
                      <span className={`weight-badge ${impact.weight > 0.7 ? 'high' : impact.weight > 0.4 ? 'medium' : 'low'}`}>
                        {Math.round(impact.weight * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phase 5: Monte Carlo Confidence Bands */}
      {simulationResults && simulationResults.length > 0 && (
        <div className="simulation-section">
          <h4>Monte Carlo Confidence Bands</h4>
          <div className="simulation-results">
            {simulationResults.map((result, idx) => {
              const riskLevel = getSimulationRiskLevel(result)
              return (
                <div key={idx} className="simulation-metric">
                  <div className="metric-header">
                    <span className="metric-name">{result.metric_name}</span>
                    <span className={`risk-indicator risk-${riskLevel}`}>{riskLevel} uncertainty</span>
                  </div>
                  <div className="confidence-bands">
                    <div className="band-chart">
                      <div className="band p10-p90" style={{ left: '0%', width: '100%' }} />
                      <div className="band p25-p75" style={{ left: '20%', width: '60%' }} />
                      <div className="band p50" style={{ left: '48%', width: '4%' }} />
                    </div>
                    <div className="band-labels">
                      <span className="band-label">p10: {result.bands.p10}{result.unit || ''}</span>
                      <span className="band-label">p25: {result.bands.p25}{result.unit || ''}</span>
                      <span className="band-label bold">p50: {result.bands.p50}{result.unit || ''}</span>
                      <span className="band-label">p75: {result.bands.p75}{result.unit || ''}</span>
                      <span className="band-label">p90: {result.bands.p90}{result.unit || ''}</span>
                    </div>
                  </div>
                  {result.current_value !== undefined && (
                    <div className="current-value">Current: {result.current_value}{result.unit || ''}</div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Tactic Changes */}
      <div className="comparison-changes">
        {/* Newly Included */}
        {comparison.differences.newly_included.length > 0 && (
          <div className="changes-section included">
            <h4>
              <span className="changes-icon">+</span>
              Newly Included in B ({comparison.differences.newly_included.length})
            </h4>
            <ul className="changes-list">
              {comparison.differences.newly_included.map((selection, idx) => (
                <li key={idx}>
                  <span className="tactic-name">{getTacticName(selection.pillar_index, selection.tactic_index)}</span>
                  <span className="pillar-name">{getPillarName(selection.pillar_index)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Newly Excluded */}
        {comparison.differences.newly_excluded.length > 0 && (
          <div className="changes-section excluded">
            <h4>
              <span className="changes-icon">-</span>
              Excluded in B ({comparison.differences.newly_excluded.length})
            </h4>
            <ul className="changes-list">
              {comparison.differences.newly_excluded.map((selection, idx) => (
                <li key={idx}>
                  <span className="tactic-name">{getTacticName(selection.pillar_index, selection.tactic_index)}</span>
                  <span className="pillar-name">{getPillarName(selection.pillar_index)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* No Changes */}
        {comparison.differences.newly_included.length === 0 &&
         comparison.differences.newly_excluded.length === 0 && (
          <div className="no-changes">
            <p>No tactic inclusion changes between these scenarios.</p>
          </div>
        )}
      </div>

      {/* BSC Coverage Comparison */}
      <div className="comparison-section">
        <h4>BSC Coverage Comparison</h4>
        <div className="bsc-comparison-grid">
          {Object.keys(comparison.metrics_a.bsc_coverage).map(perspective => {
            const valueA = comparison.metrics_a.bsc_coverage[perspective]
            const valueB = comparison.metrics_b.bsc_coverage[perspective]
            const delta = valueB - valueA

            return (
              <div key={perspective} className="bsc-comparison-row">
                <span className="bsc-perspective">{perspective}</span>
                <div className="bsc-values">
                  <span className="bsc-value-a">{valueA}%</span>
                  <span className="bsc-arrow">→</span>
                  <span className="bsc-value-b">{valueB}%</span>
                  {delta !== 0 && (
                    <span className={`bsc-delta ${delta > 0 ? 'positive' : 'negative'}`}>
                      ({delta > 0 ? '+' : ''}{delta})
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Summary */}
      <div className="comparison-summary">
        <h4>Summary</h4>
        <p>
          {comparison.differences.coverage_delta > 0 ? (
            <>Scenario B has <strong>{comparison.differences.coverage_delta}% higher coverage</strong></>
          ) : comparison.differences.coverage_delta < 0 ? (
            <>Scenario B has <strong>{Math.abs(comparison.differences.coverage_delta)}% lower coverage</strong></>
          ) : (
            <>Both scenarios have the same coverage</>
          )}
          {comparison.differences.risk_delta !== 0 && (
            <>
              {' '}with a{' '}
              <strong className={comparison.differences.risk_delta < 0 ? 'positive' : 'negative'}>
                {comparison.differences.risk_delta > 0 ? 'higher' : 'lower'} risk profile
              </strong>
            </>
          )}.
        </p>
      </div>
    </div>
  )
}

export default ScenarioComparisonView
